import { Component, EventEmitter, Input, Output, OnChanges, OnInit, SimpleChanges } from '@angular/core';

import { IconComponent } from '../icon/icon.component';
import { CourseNavigationItem, PlayerMode } from '../../../services/course-player.service';
import { ProfileService } from '../../../profile/profile.service';
import { roundCourseProgress } from '../../utils/progress-display.utils';
import { estimateDurationLabel } from '../../utils/duration-estimate.utils';
import { computeRunningScore, RunningScoreSummary } from '../../utils/running-score.utils';

@Component({
  selector: 'app-course-timeline',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './course-timeline.component.html',
  styleUrls: ['./course-timeline.component.scss']
})
export class CourseTimelineComponent implements OnChanges, OnInit {
  @Input() navigationItems: CourseNavigationItem[] = [];
  @Input() currentItemId: string = '';
  @Input() courseName: string = '';
  @Input() playerMode: PlayerMode = 'learner';
  @Input() showProgress: boolean = true;
  @Input() allowJumpNavigation: boolean = false;
  // KAN-411 round 4: genuine course completion (modules done AND pass gate
  // satisfied), from the parent modal. Gates the course-summary nav entry's
  // "Course Complete" label so a not-passed course doesn't claim completion in
  // the sidebar (contradicting the "Not yet passed" summary badge).
  @Input() courseComplete: boolean = false;
  // KAN-465: learner progress rows (one entry per quiz, best submission) and
  // the course passing threshold, for the running cumulative points readout.
  @Input() courseProgressMap: Map<string, any> = new Map();
  @Input() coursePassingThreshold: number | null = null;

  @Output() onItemSelect = new EventEmitter<CourseNavigationItem>();
  @Output() onClose = new EventEmitter<void>();

  // Timeline state
  expandedUnits: Set<string> = new Set();
  // KAN-607: the item whose unit was last auto-expanded. Auto-expand fires once
  // per current-item change, so a learner's manual collapse holds until they
  // move to another item.
  private autoExpandedForItemId: string | null = null;
  progressSummary = {
    completed: 0,
    total: 0,
    percentage: 0
  };

  // Debug email line — shows the signed-in user + current view directly below
  // the course progress bar. Instructor modes only (KAN-672); on by default
  // there, toggleable per session.
  showDebugEmail = true;

  constructor(private profileService: ProfileService) {}

  /**
   * Email of the currently signed-in user (for the debug line).
   * Falls back to 'unknown' when no user is resolvable.
   */
  get currentUserEmail(): string {
    return this.profileService.getCurrentUser()?.email || 'unknown';
  }

  /**
   * Human-readable label for the current player view (for the debug line).
   */
  get debugViewLabel(): string {
    switch (this.playerMode) {
      case 'instructor_preview':
        return 'Instructor Preview';
      case 'instructor_test':
        return 'Instructor Test';
      default:
        return 'Learner';
    }
  }

  /**
   * KAN-672: the debug line (and its "Show debug" button) is an instructor
   * aid. Learners never see it, whatever showDebugEmail says.
   */
  get debugLineAvailable(): boolean {
    return this.playerMode !== 'learner';
  }

  /**
   * Toggle visibility of the debug email line.
   */
  toggleDebugEmail(): void {
    this.showDebugEmail = !this.showDebugEmail;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // KAN-607: the learner player mounts the timeline on the landing page (no
    // current item yet), so seeding only in ngOnInit never opened a unit.
    if (changes['currentItemId'] || changes['navigationItems']) {
      this.expandCurrentUnit();
    }
  }

  ngOnInit() {
    this.calculateProgress();
    this.expandCurrentUnit();
  }

  /**
   * KAN-465: running cumulative points score over the attempted quizzes.
   * A getter (like getGroupedItems) so it always reflects the current map —
   * the parent replaces/mutates courseProgressMap and drives CD when progress
   * changes. Math lives in the pure computeRunningScore util.
   */
  get runningScore(): RunningScoreSummary {
    return computeRunningScore(this.courseProgressMap.values(), this.coursePassingThreshold);
  }

  /**
   * Calculate overall progress
   * Public so it can be called externally when navigation item statuses change
   */
  public calculateProgress(): void {
    // Filter playable items but exclude course_summary from progress calculation
    const playableItems = this.navigationItems.filter(item =>
      item.isPlayable && item.type !== 'course_summary'
    );
    const completed = playableItems.filter(item => item.status === 'completed');

    this.progressSummary = {
      completed: completed.length,
      total: playableItems.length,
      // KAN-146 AC3: use the canonical 1-dp rule so the timeline header agrees
      // with every backend-emitted progress surface (no integer re-rounding).
      percentage: roundCourseProgress(completed.length, playableItems.length)
    };

    console.log('[CourseTimeline] Progress calculated:', this.progressSummary, {
      playableItems: playableItems.length,
      completedItems: completed.length,
      completedItemIds: completed.map(item => item.id)
    });
  }

  /**
   * KAN-607: expand the unit containing the current item, once per item. Other
   * units keep whatever state the learner gave them.
   */
  private expandCurrentUnit(): void {
    if (!this.currentItemId) {
      this.autoExpandedForItemId = null;
      return;
    }
    if (this.currentItemId === this.autoExpandedForItemId) return;
    const currentItem = this.navigationItems.find(item => item.id === this.currentItemId);
    // Items not loaded yet: retry when navigationItems arrives.
    if (!currentItem) return;
    this.autoExpandedForItemId = this.currentItemId;
    if (currentItem.parentUnitId) {
      this.expandedUnits.add(currentItem.parentUnitId);
    }
  }

  /**
   * Toggle unit expansion
   */
  toggleUnit(unitId: string): void {
    if (this.expandedUnits.has(unitId)) {
      this.expandedUnits.delete(unitId);
    } else {
      this.expandedUnits.add(unitId);
    }
  }

  /**
   * Check if unit is expanded
   */
  isUnitExpanded(unitId: string): boolean {
    return this.expandedUnits.has(unitId);
  }

  /**
   * KAN-339: bulk collapse/expand for the timeline. Mirrors the instructor
   * course-builder affordance (unfold_less / unfold_more) so both surfaces
   * feel consistent. Operates only over the unit ids from getGroupedItems();
   * the per-node toggleUnit behavior is left untouched.
   */

  /** All unit ids present in the current grouped structure. */
  private getUnitIds(): string[] {
    return this.getGroupedItems()
      .filter(group => group.type === 'unit' && !!group.unitId)
      .map(group => group.unitId as string);
  }

  /** True when the timeline has at least one collapsible unit. */
  get hasUnits(): boolean {
    return this.getUnitIds().length > 0;
  }

  /**
   * True only when every unit is expanded. Returns false when there are no
   * units so the toggle offers "Expand all" (the button is hidden via
   * hasUnits in that case anyway).
   */
  get allUnitsExpanded(): boolean {
    const unitIds = this.getUnitIds();
    return unitIds.length > 0 && unitIds.every(id => this.expandedUnits.has(id));
  }

  /** Expand every unit node. */
  expandAll(): void {
    for (const unitId of this.getUnitIds()) {
      this.expandedUnits.add(unitId);
    }
  }

  /** Collapse every unit node. */
  collapseAll(): void {
    this.expandedUnits.clear();
  }

  /**
   * Single toggle button: collapse all when everything is expanded, else
   * expand all. Drives the header affordance's label/icon.
   */
  toggleAllUnits(): void {
    if (this.allUnitsExpanded) {
      this.collapseAll();
    } else {
      this.expandAll();
    }
  }

  /**
   * Get items grouped by units while maintaining course structure order
   */
  getGroupedItems(): Array<{
    type: 'direct' | 'unit';
    unitId?: string;
    unitName?: string;
    unitDescription?: string;
    items: CourseNavigationItem[];
    hasUnitQuiz?: boolean;
  }> {
    const groups: Array<{
      type: 'direct' | 'unit';
      unitId?: string;
      unitName?: string;
      unitDescription?: string;
      items: CourseNavigationItem[];
      hasUnitQuiz?: boolean;
    }> = [];
    
    // Track what we've already processed
    const processedItems = new Set<string>();
    const processedUnits = new Set<string>();
    let currentDirectModuleGroup: CourseNavigationItem[] = [];
    
    // Process items in their original order
    for (const item of this.navigationItems) {
      if (processedItems.has(item.id)) {
        continue;
      }
      
      // If this is a direct module
      if (item.isDirectModule) {
        // Add to current direct module group
        currentDirectModuleGroup.push(item);
        processedItems.add(item.id);
      } 
      // If this is part of a unit
      else if (item.parentUnitId && !processedUnits.has(item.parentUnitId)) {
        // First, flush any pending direct modules
        if (currentDirectModuleGroup.length > 0) {
          groups.push({
            type: 'direct',
            items: [...currentDirectModuleGroup]
          });
          currentDirectModuleGroup = [];
        }
        
        // Process the entire unit
        const unitId = item.parentUnitId;
        const unitItems = this.navigationItems.filter(unitItem => unitItem.parentUnitId === unitId);
        const unitIntro = unitItems.find(unitItem => unitItem.type === 'unit_intro');
        const hasUnitQuiz = unitItems.some(unitItem => unitItem.type === 'unit_quiz');
        
        // Get unit name from any item in the unit (they all have parentUnitName)
        const firstUnitItem = unitItems[0];
        const unitName = firstUnitItem?.parentUnitName ||
                         unitIntro?.name.replace(' - Overview', '') ||
                         `Unit ${unitId}`;

        groups.push({
          type: 'unit',
          unitId,
          unitName,
          unitDescription: unitIntro?.name || '',
          items: unitItems,
          hasUnitQuiz
        });
        
        // Mark all unit items as processed
        unitItems.forEach(unitItem => processedItems.add(unitItem.id));
        processedUnits.add(unitId);
      }
    }
    
    // Flush any remaining direct modules
    if (currentDirectModuleGroup.length > 0) {
      groups.push({
        type: 'direct',
        items: currentDirectModuleGroup
      });
    }

    return groups;
  }

  /**
   * Handle item click
   */
  selectItem(item: CourseNavigationItem): void {
    // Check if item can be accessed
    if (!this.canAccessItem(item)) {
      return;
    }

    // For unit intro items, expand the unit and navigate
    if (item.type === 'unit_intro') {
      if (item.parentUnitId) {
        this.expandedUnits.add(item.parentUnitId);
      }
      // Still emit the selection event to navigate to the intro page
      this.onItemSelect.emit(item);
      return;
    }

    this.onItemSelect.emit(item);
  }

  canAccessItem(item: CourseNavigationItem): boolean {
    if (this.allowJumpNavigation) {
      return true;
    }

    if (item.type === 'unit_intro') {
      return true;
    }

    return item.isPlayable && item.status !== 'locked';
  }

  /**
   * Get status icon for item
   */
  getItemIcon(item: CourseNavigationItem): string {
    switch (item.status) {
      case 'completed':
        return 'check_circle';
      case 'current':
        return 'play_circle';
      case 'locked':
        return 'lock';
      default:
        // Available status - show content type icon
        if (item.type === 'course_summary') return 'school';
        if (item.type === 'unit_intro') return 'info';
        if (item.type === 'unit_quiz') return 'quiz';
        if (item.hasVideo && item.hasQuiz) return 'video_library';
        if (item.hasVideo) return 'play_circle_outline';
        if (item.hasQuiz) return 'quiz';
        return 'article';
    }
  }

  /**
   * Get status color class for item
   */
  getItemStatusClass(item: CourseNavigationItem): string {
    switch (item.status) {
      case 'completed':
        return 'text-success';
      case 'current':
        return 'text-primary';
      case 'locked':
        return 'text-base-content/30';
      default:
        return 'text-base-content/70';
    }
  }

  /**
   * Get unit completion status
   */
  getUnitProgress(unitItems: CourseNavigationItem[]): { completed: number; total: number; percentage: number } {
    const playableItems = unitItems.filter(item => item.isPlayable);
    const completed = playableItems.filter(item => item.status === 'completed');
    
    return {
      completed: completed.length,
      total: playableItems.length,
      // KAN-146 AC3: canonical 1-dp rule (matches calculateProgress + backend).
      percentage: roundCourseProgress(completed.length, playableItems.length)
    };
  }

  /**
   * Check if current item is in this unit
   */
  isCurrentItemInUnit(unitId: string): boolean {
    const currentItem = this.navigationItems.find(item => item.id === this.currentItemId);
    return currentItem?.parentUnitId === unitId;
  }

  /**
   * Navigate to unit intro (if it exists)
   */
  navigateToUnitIntro(unitId: string, event: Event): void {
    event.stopPropagation(); // Prevent unit toggle

    // Find the unit_intro item for this unit
    const unitIntro = this.navigationItems.find(
      item => item.type === 'unit_intro' && item.parentUnitId === unitId
    );

    if (unitIntro && this.canAccessItem(unitIntro)) {
      this.selectItem(unitIntro);
    } else {
      console.warn('[CourseTimeline] No accessible unit intro found for unit:', unitId);
    }
  }

  /**
   * Check if unit has an intro page
   */
  hasUnitIntro(unitId: string): boolean {
    return this.navigationItems.some(
      item => item.type === 'unit_intro' && item.parentUnitId === unitId
    );
  }

  /**
   * Close timeline
   */
  close(): void {
    this.onClose.emit();
  }

  /**
   * Get display text for item type
   */
  getItemTypeText(item: CourseNavigationItem): string {
    switch (item.type) {
      case 'course_summary':
        // KAN-411 round 4: only claim "Course Complete" when the course genuinely
        // completed; a not-passed course shows the neutral "Summary" label (the
        // "View Results" subtitle still applies - results are viewable either way).
        return this.courseComplete ? 'Course Complete' : 'Summary';
      case 'unit_intro':
        return 'Overview';
      case 'unit_quiz':
        return 'Unit Quiz';
      case 'module':
        if (item.hasVideo && item.hasQuiz) return 'Video + Quiz';
        if (item.hasVideo) return 'Video';
        if (item.hasQuiz) return 'Quiz';
        return 'Module';
      default:
        return item.type;
    }
  }

  /**
   * KAN-464: time estimate preferring real data (video duration + question
   * count); legacy shape-based ranges only when the video duration is NULL.
   * Logic lives in shared/utils/duration-estimate.utils.ts.
   */
  getEstimatedDuration(item: CourseNavigationItem): string {
    return estimateDurationLabel(item);
  }

  /**
   * Get video display info (Video name • runtime)
   */
  getVideoInfo(item: CourseNavigationItem): string {
    if (!item.hasVideo) return '';

    const videoName = item.videoTitle || 'Video';
    const duration = item.videoDuration
      ? this.formatDuration(item.videoDuration)
      : null;

    return duration ? `${videoName} • ${duration}` : videoName;
  }

  /**
   * Get quiz display info (Quiz title • question count)
   */
  getQuizInfo(item: CourseNavigationItem): string {
    if (!item.hasQuiz || !item.quiz) return '';
    const title = item.quizTitle || 'Module Quiz';
    const questionCount = item.quiz.length;
    const questionText = questionCount === 1 ? 'question' : 'questions';
    return `${title} • ${questionCount} ${questionText}`;
  }

  /**
   * Pluralized "N question(s)" label for quiz cards. Returns null when no count
   * is available so the template can omit the label.
   */
  getQuestionCountLabel(item: CourseNavigationItem): string | null {
    if (!item.hasQuiz) return null;
    if (item.type === 'unit_intro') return null;
    const count = item.quiz?.length ?? 0;
    if (count <= 0) return null;
    return `${count} ${count === 1 ? 'question' : 'questions'}`;
  }

  /**
   * Format duration from seconds to readable format
   */
  private formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    if (minutes < 1) {
      return '<1 min';
    }
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  }

  /**
   * Track by function for *ngFor optimization
   */
  trackByItemId(index: number, item: CourseNavigationItem): string {
    return item.id;
  }

  /**
   * Track by function for units
   */
  trackByUnitId(index: number, group: any): string {
    return group.unitId || `direct-${index}`;
  }

  /**
   * Get playable items count for a group
   */
  getPlayableItemsCount(group: any): number {
    return group.items.filter((i: any) => i.isPlayable).length;
  }
}